/**
 * IncassoListPage
 *
 * List view for incasso dossiers with page-based pagination.
 * Uses cached DB columns for fast loads (no Odoo roundtrip).
 * Preserves all original functionality from IncassoSection:
 * - Brief (payment reminder letter)
 * - Dossier detail page navigation (click amount/invoices → /rental/incasso/dossier/:id)
 * - NotesEdit modal (contact/company notes)
 * - Legal procedure pills (case numbers + next dates)
 * - Payment plan indicators
 * - TwilioCallButton + DevWhatsAppButton
 * - BeoordelingPopover + SnoozeIndicator
 * - Clickable KPI stats (payment plan, legal, snoozed)
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession, useUser } from '@clerk/clerk-react';
import { cn } from '@/lib/utils';
import { ListBulletsIcon, KanbanIcon, ArrowSquareOut, Info, ArrowsClockwise, DownloadSimple, DotsThreeVertical } from '@phosphor-icons/react';
import { PixelLogo } from 'shared/components/PixelLogo';
import { Outgoing } from 'assets/icons';
import ButtonNew from 'components/buttonNew/buttonNew';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { FilterBar } from 'shared/components/filterBar';
import { UniversalTable } from 'shared/components/UniversalTable';
import { TablePagination } from 'components/TablePagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { IncassoStatsBar } from '../components/IncassoStatsBar';
import { IncassoDebtChart } from '../components/IncassoDebtChart';
import { SnoozeDialog } from '../components/SnoozeDialog';
import { PhaseTransitionDialog } from '../components/PhaseTransitionDialog';
import { useIncassoShared } from '../shared/hooks/useIncassoShared';
import { isBackwardMove, getPhaseLabel } from '../configs/incassoPhaseConfig';
import { IncassoTicket, IncassoBeoordeling, IncassoPhase } from '../shared/incassoTypes';
import { API_URL } from 'constants/index';
import {
  NotesEditModal,
  IncassoActionModal,
  LegalProcedureModal,
  PaymentPlanModal,
} from 'shared/components/rental/sections/incasso/components';
import { CustomerInvoice } from 'shared/types/customer-invoice';
import { LegalProcedureSummary } from 'shared/types/legal-procedure';
import { PaymentPlanSummary } from 'shared/types/payment-plan';
import {
  INCASSO_LIST_COLUMNS,
  createIncassoListCells,
  IncassoListCellContext,
} from './IncassoListCells';
import type { TableSortConfig } from 'shared/components/UniversalTable/types/SortConfig';

// ─── Constants ───────────────────────────────────────────────────────────────

const INCASSO_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: 'cachedTotalDebt',
  defaultDirection: 'desc',
  fields: [
    {
      key: 'cachedTotalDebt',
      extractor: (row: IncassoTicket) => Number(row.cachedTotalDebt) || 0,
    },
    {
      key: 'cachedMaxDaysOverdue',
      extractor: (row: IncassoTicket) => Number(row.cachedMaxDaysOverdue) || 0,
    },
  ],
};

const PAGE_SIZE = 50;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Convert IncassoTicket to CustomerInvoice shape for legacy modals */
const ticketToInvoice = (ticket: IncassoTicket | null): CustomerInvoice | null => {
  if (!ticket) return null;
  return {
    id: Number(ticket.id),
    name: '',
    amount_residual: Number(ticket.cachedTotalDebt) || 0,
    contact: ticket.contact
      ? {
          id: ticket.contact.id,
          firstName: ticket.contact.firstName,
          lastName: ticket.contact.lastName,
          phone_mobile: ticket.contact.phone_mobile || undefined,
          phone_direct: ticket.contact.phone_direct || undefined,
          notes: ticket.contact.notes,
          email: ticket.contact.email || '',
          jobTitle: '',
        }
      : null,
    company: ticket.company
      ? {
          id: ticket.company.id,
          name: ticket.company.name,
          phone_direct: ticket.company.phone_direct,
          notes: ticket.company.notes,
        }
      : null,
    unit: null,
    partner_id: [ticket.odooPartnerId, ticket.odooPartnerDisplayName],
    invoice_partner_display_name: ticket.odooPartnerDisplayName,
    x_studio_sub_unit: '',
    invoice_date: null,
    invoice_date_due: null,
    invoice_days_due: String(ticket.cachedMaxDaysOverdue || 0),
    analytic_account: ticket.cachedBuildingName || '',
    incassoActionCount: ticket.incassoActionCount || 0,
  };
};

// ─── Component ───────────────────────────────────────────────────────────────

const IncassoListPageComponent = () => {
  const navigate = useNavigate();
  const { session } = useSession();
  const { user } = useUser();

  // Stable ref for session — avoids re-triggering fetches on tab focus
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const state = useIncassoShared('list');
  const {
    searchText,
    setSearchText,
    apiFilters,
    isRestoringFromURL,
    stats,
    filterBarSections,
    filterBarChips,
    handleFiltersFromURL,
    handlePhaseChange,
    handleSnooze,
    handleUnsnooze,
    handleBeoordelingChange,
    fetchStats,
    totalUnpaidRent,
    hasSynced,
    snoozeModal,
    setSnoozeModal,
    phaseTransitionPending,
    setPhaseTransitionPending,
    actionModal,
    setActionModal,
    legalModal,
    setLegalModal,
    paymentPlanModal,
    setPaymentPlanModal,
    handlePaymentPlanStatsClick,
    handleLegalProcedureStatsClick,
    handleSnoozedStatsClick,
    handleIncassoViaDerdeStatsClick,
    isRefreshing,
    handleRefresh,
    isExporting,
    handleExportToExcel,
  } = state;

  // ─── Page-based data state ──────────────────────────────────────────────
  const [tickets, setTickets] = useState<IncassoTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const sortBy = 'cachedTotalDebt';
  const sortDirection = 'DESC';

  // ─── Letter generation state ────────────────────────────────────────────
  const [generatingLetters, setGeneratingLetters] = useState<Record<number, boolean>>({});
  const [generatedLetterUrls, setGeneratedLetterUrls] = useState<Record<number, string>>({});

  // ─── Notes modal state ──────────────────────────────────────────────────
  const [notesInvoice, setNotesInvoice] = useState<CustomerInvoice | null>(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  // ─── Enrichment data (fetched after tickets load) ───────────────────────
  const [legalSummaries, setLegalSummaries] = useState<Record<number, LegalProcedureSummary[]>>({});
  const [paymentPlanSummaries, setPaymentPlanSummaries] = useState<Record<number, PaymentPlanSummary[]>>({});
  const [emailCounts, setEmailCounts] = useState<Record<number, { total: number; unread: number }>>({});

  // ─── Fetch helpers (use sessionRef to avoid re-triggering on tab focus) ──

  const fetchLegalSummaries = useCallback(async (partnerIds: number[]) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/legal-procedures/summaries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partnerIds }),
        },
        sessionRef.current,
      );
      if (response.ok) {
        setLegalSummaries(await response.json());
      }
    } catch (error) {
      console.error('[IncassoListPage] Error fetching legal summaries:', error);
    }
  }, []);

  const fetchEmailCounts = useCallback(async (partnerIds: number[]) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/email/incasso/counts-by-partner`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partnerIds }),
        },
        sessionRef.current,
      );
      if (response.ok) {
        setEmailCounts(await response.json());
      }
    } catch (error) {
      console.error('[IncassoListPage] Error fetching incasso email counts:', error);
    }
  }, []);

  const fetchPaymentPlanSummaries = useCallback(async (partnerIds: number[]) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/payment-plans/summaries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partnerIds }),
        },
        sessionRef.current,
      );
      if (response.ok) {
        setPaymentPlanSummaries(await response.json());
      }
    } catch (error) {
      console.error('[IncassoListPage] Error fetching payment plan summaries:', error);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    if (!sessionRef.current) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        sortBy,
        sortDirection,
      });
      if (apiFilters.search) params.set('search', apiFilters.search);
      if (apiFilters.phase) params.set('phase', apiFilters.phase);
      if (apiFilters.beoordeling) params.set('beoordeling', apiFilters.beoordeling);
      if (apiFilters.buildingNames?.length) params.set('buildingNames', apiFilters.buildingNames.join(','));
      if (apiFilters.companyNames?.length) params.set('companyNames', apiFilters.companyNames.join(','));
      if (apiFilters.debtBucket) params.set('debtBucket', apiFilters.debtBucket);
      if (apiFilters.includeSnoozed !== undefined) {
        params.set('includeSnoozed', String(apiFilters.includeSnoozed));
      }
      if (apiFilters.noIncassoActions) params.set('noIncassoActions', 'true');

      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets?${params}`,
        { method: 'GET' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error(`Failed to fetch tickets: ${response.status}`);

      const result = await response.json();
      const ticketData: IncassoTicket[] = result.data ?? [];
      setTickets(ticketData);
      setTotal(result.total ?? 0);

      // Fetch enrichment data for all partners on this page
      const partnerIds = [...new Set(ticketData.map((t) => t.odooPartnerId).filter(Boolean))];
      if (partnerIds.length > 0) {
        fetchLegalSummaries(partnerIds);
        fetchPaymentPlanSummaries(partnerIds);
        fetchEmailCounts(partnerIds);
      }
    } catch (error) {
      console.error('[IncassoListPage] Error fetching tickets:', error);
      toast.error('Fout bij ophalen van dossiers');
    } finally {
      setIsLoading(false);
    }
  }, [page, sortBy, sortDirection, apiFilters, fetchLegalSummaries, fetchPaymentPlanSummaries, fetchEmailCounts]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [apiFilters.search, apiFilters.phase, apiFilters.beoordeling, apiFilters.debtBucket, apiFilters.includeSnoozed, apiFilters.noIncassoActions]);

  // Fetch when page/sort/filters change, and after initial sync
  useEffect(() => {
    if (hasSynced) fetchTickets();
  }, [hasSynced, fetchTickets]);

  // ─── Beoordeling handler ────────────────────────────────────────────────
  const handleBeoordelingUpdate = useCallback(
    async (ticket: IncassoTicket, beoordeling: IncassoBeoordeling | null, reden?: string) => {
      try {
        await handleBeoordelingChange(ticket, beoordeling, reden);
        setTickets((prev) => prev.map((t) => (
          t.id === ticket.id
            ? { ...t, beoordeling, beoordelingReden: reden ?? null }
            : t
        )));
      } catch { /* Error already toasted */ }
    },
    [handleBeoordelingChange],
  );

  // ─── Phase change handler (same logic as kanban drag-drop) ─────────────
  const DIALOG_PHASES = useMemo(() => new Set<IncassoPhase>([
    IncassoPhase.MINNELIJKE_REGELING,
    IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN,
  ]), []);

  const handlePhasePickerChange = useCallback(
    (ticket: IncassoTicket, newPhase: IncassoPhase) => {
      if (newPhase === ticket.phase) return;

      const needsDialog = DIALOG_PHASES.has(newPhase) || isBackwardMove(ticket.phase, newPhase);

      if (needsDialog) {
        setPhaseTransitionPending({
          ticket,
          fromPhase: ticket.phase,
          toPhase: newPhase,
        });
        return;
      }

      // Direct change — optimistic update + API
      setTickets((prev) =>
        prev.map((t) => (t.id === ticket.id ? { ...t, phase: newPhase } : t)),
      );
      toast.success(`Dossier verplaatst naar "${getPhaseLabel(newPhase)}"`);

      handlePhaseChange(String(ticket.id), newPhase)
        .then(() => { fetchStats(); fetchTickets(); })
        .catch(() => {
          setTickets((prev) =>
            prev.map((t) => (t.id === ticket.id ? { ...t, phase: ticket.phase } : t)),
          );
        });
    },
    [DIALOG_PHASES, handlePhaseChange, setPhaseTransitionPending, fetchStats, fetchTickets],
  );

  const handlePhaseTransitionConfirm = useCallback(
    async (createRelated: boolean) => {
      if (!phaseTransitionPending) return;
      const { ticket, toPhase } = phaseTransitionPending;

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) => (t.id === ticket.id ? { ...t, phase: toPhase } : t)),
      );
      toast.success(`Dossier verplaatst naar "${getPhaseLabel(toPhase)}"`);
      setPhaseTransitionPending(null);

      // Persist
      await handlePhaseChange(String(ticket.id), toPhase);

      // Open related modal if requested
      if (createRelated) {
        if (toPhase === IncassoPhase.MINNELIJKE_REGELING) {
          setPaymentPlanModal(ticket);
        } else if (toPhase === IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN) {
          setLegalModal(ticket);
        }
      }

      fetchStats();
      fetchTickets();
    },
    [phaseTransitionPending, handlePhaseChange, setPhaseTransitionPending, setPaymentPlanModal, setLegalModal, fetchStats, fetchTickets],
  );

  // ─── Generate payment reminder letter ──────────────────────────────────
  const handleGenerateLetter = useCallback(
    async (ticket: IncassoTicket) => {
      const id = Number(ticket.id);
      if (generatingLetters[id] || !ticket.contact?.id) return;

      setGeneratingLetters((prev) => ({ ...prev, [id]: true }));
      try {
        const response = await fetchWithAuth(
          `${API_URL}/documents/generate-outstanding-invoice-letter`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contactId: ticket.contact.id }),
          },
          sessionRef.current,
        );
        if (!response.ok) throw new Error('Failed to generate outstanding invoice letter');
        const result = await response.json();
        setGeneratedLetterUrls((prev) => ({ ...prev, [id]: result.fileUrl }));
        toast.success('Betalingsherinnering succesvol gegenereerd!');
      } catch (error) {
        console.error('[IncassoListPage] Error generating letter:', error);
        toast.error('Fout bij het genereren van de betalingsherinnering.');
      } finally {
        setGeneratingLetters((prev) => ({ ...prev, [id]: false }));
      }
    },
    [generatingLetters],
  );

  // ─── Log phone call as incasso action ──────────────────────────────────
  const handleLogPhoneCall = useCallback(
    async (ticket: IncassoTicket, phoneNumber: string, durationSeconds?: number) => {
      if (!user?.id) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-actions/log-phone-call`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              odooPartnerId: ticket.odooPartnerId,
              phoneNumber,
              createdByUserId: user.id,
              contactId: ticket.contact?.id,
              companyId: ticket.company?.id,
              durationSeconds,
            }),
          },
          sessionRef.current,
        );
        if (response.ok) {
          setTickets((prev) =>
            prev.map((t) =>
              t.id === ticket.id
                ? { ...t, incassoActionCount: (t.incassoActionCount || 0) + 1 }
                : t,
            ),
          );
          toast.success('Telefoongesprek gelogd');
        }
      } catch (error) {
        console.error('[IncassoListPage] Error logging phone call:', error);
        toast.error('Fout bij het loggen van telefoongesprek');
      }
    },
    [user],
  );

  // ─── Notes handlers ────────────────────────────────────────────────────
  const handleEditNotes = useCallback((ticket: IncassoTicket) => {
    setNotesInvoice(ticketToInvoice(ticket));
    setIsNotesModalOpen(true);
  }, []);

  const handleNotesUpdated = useCallback((invoice: CustomerInvoice, notes: string) => {
    setTickets((prev) =>
      prev.map((t) => {
        if (t.odooPartnerId !== invoice.partner_id?.[0]) return t;
        if (t.contact && invoice.contact) {
          return { ...t, contact: { ...t.contact, notes } };
        }
        if (t.company && invoice.company) {
          return { ...t, company: { ...t.company, notes } };
        }
        return t;
      }),
    );
  }, []);

  // ─── Open dossier detail page ──────────────────────────────────────────
  // Replaces the old DossierDetailSheet drawer. Navigates to the full-page
  // route which mirrors the helpdesk detail page structure.
  const handleOpenDetailSheet = useCallback(
    (ticket: IncassoTicket) => {
      navigate(`/rental/incasso/dossier/${ticket.id}`);
    },
    [navigate],
  );

  // ─── Modal close refresh ───────────────────────────────────────────────
  const handleModalClose = useCallback(() => {
    fetchStats();
    fetchTickets();
  }, [fetchStats, fetchTickets]);

  // ─── Cell context ──────────────────────────────────────────────────────
  const cellContext: IncassoListCellContext = useMemo(
    () => ({
      generatingLetters,
      generatedLetterUrls,
      legalSummaries,
      paymentPlanSummaries,
      emailCounts,
      onGenerateLetter: handleGenerateLetter,
      onEditNotes: handleEditNotes,
      onOpenDetailSheet: handleOpenDetailSheet,
      onViewIncassoActions: (t) => setActionModal(t),
      onViewLegalProcedures: (t) => setLegalModal(t),
      onViewPaymentPlans: (t) => setPaymentPlanModal(t),
      onLogPhoneCall: handleLogPhoneCall,
      onBeoordelingChange: handleBeoordelingUpdate,
      onPhaseChange: handlePhasePickerChange,
      onSnooze: (t) => setSnoozeModal({ open: true, ticket: t }),
    }),
    [
      generatingLetters, generatedLetterUrls, legalSummaries, paymentPlanSummaries,
      emailCounts,
      handleGenerateLetter, handleEditNotes, handleOpenDetailSheet,
      setActionModal, setLegalModal, setPaymentPlanModal, handleLogPhoneCall, handleBeoordelingUpdate,
      handlePhasePickerChange, setSnoozeModal,
    ],
  );

  // ─── Row render ────────────────────────────────────────────────────────
  const renderRow = useCallback(
    (ticket: IncassoTicket) => createIncassoListCells(ticket, cellContext),
    [cellContext],
  );

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0 min-h-screen">
        {/* Header */}
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-gray-900">Incasso Dossiers</h1>
              <p className="text-xs text-neutral-secondary-mid mt-1 flex items-center gap-1">
                <Info size={12} weight="fill" className="shrink-0" />
                Dossiers verschijnen pas na 15 dagen te laat. Gesnoozede dossiers worden automatisch weer zichtbaar na de snoozedatum.
              </p>
            </div>
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center gap-2">
                {/* Refresh + last snapshot tooltip */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={async () => {
                        await handleRefresh();
                        fetchTickets();
                      }}
                      disabled={isRefreshing}
                      className={cn(
                        'flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-medium transition-colors',
                        isRefreshing
                          ? 'cursor-not-allowed text-neutral-400'
                          : 'text-neutral-700 hover:bg-neutral-50',
                      )}
                    >
                      <ArrowsClockwise
                        size={14}
                        className={isRefreshing ? 'animate-spin' : ''}
                      />
                      <span className="hidden sm:inline">
                        {isRefreshing ? 'Vernieuwen...' : 'Vernieuwen'}
                      </span>
                    </button>
                  </TooltipTrigger>
                  {stats?.lastSnapshotAt && (
                    <TooltipContent side="bottom">
                      Laatste snapshot:{' '}
                      {new Date(stats.lastSnapshotAt).toLocaleString('nl-BE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TooltipContent>
                  )}
                </Tooltip>

                {/* Open in Odoo */}
                <ButtonNew
                  variant="secondaryNeutral"
                  icon={<Outgoing />}
                  label="Open in Odoo"
                  href="https://portal.yally.be/web#action=270&model=account.move&view_type=list&menu_id=291&cids=1%2C3%2C2%2C4"
                  target="_blank"
                />

                {/* More menu: external tools + export */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-700 transition-colors hover:bg-neutral-50"
                      aria-label="Meer acties"
                      title="Meer acties"
                    >
                      <DotsThreeVertical size={18} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Externe tools</DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <a
                        href="https://territoriale-bevoegdheid.just.fgov.be"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex cursor-pointer items-center justify-between gap-2"
                      >
                        Vredegerecht
                        <ArrowSquareOut size={14} className="text-neutral-secondary-mid" />
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a
                        href="https://access.eservices.just.fgov.be/edeposit.nl/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex cursor-pointer items-center justify-between gap-2"
                      >
                        E-Deposit
                        <ArrowSquareOut size={14} className="text-neutral-secondary-mid" />
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Exporteren</DropdownMenuLabel>
                    <DropdownMenuItem
                      disabled={isExporting}
                      onSelect={(e) => {
                        e.preventDefault();
                        handleExportToExcel();
                      }}
                      className="flex cursor-pointer items-center justify-between gap-2"
                    >
                      {isExporting ? 'Exporteren...' : 'Exporteer naar Excel'}
                      <DownloadSimple size={14} className="text-neutral-secondary-mid" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* View toggle */}
                <div className="border-input bg-background flex items-center gap-1 rounded-lg border p-1 shadow-sm">
                  <button
                    onClick={() => navigate('/rental/incasso/list')}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                      'bg-moss-primary text-white shadow-sm',
                    )}
                    aria-label="Lijstweergave"
                    title="Lijstweergave"
                  >
                    <ListBulletsIcon size={18} weight="fill" />
                    <span className="hidden sm:inline">Lijst</span>
                  </button>
                  <button
                    onClick={() => navigate('/rental/incasso/kanban')}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                      'text-muted-foreground hover:text-foreground hover:bg-muted',
                    )}
                    aria-label="Kanbanweergave"
                    title="Kanbanweergave"
                  >
                    <KanbanIcon size={18} weight="regular" />
                    <span className="hidden sm:inline">Kanban</span>
                  </button>
                </div>
              </div>
            </TooltipProvider>
          </div>
        </header>

        {/* Stats bar */}
        {stats && (
          <div className="px-6 pb-4">
            <IncassoStatsBar
              totalUnpaidRent={totalUnpaidRent}
              totalOutstanding={stats.totalOutstanding}
              activeCases={stats.activeCases}
              snoozedCases={stats.snoozedCases}
              paymentPlanAmount={stats.paymentPlanAmount}
              legalProcedureAmount={stats.legalProcedureAmount}
              snoozedAmount={stats.snoozedAmount}
              incassoViaDerdeAmount={stats.incassoViaDerdeAmount}
              onPaymentPlanClick={handlePaymentPlanStatsClick}
              onLegalProcedureClick={handleLegalProcedureStatsClick}
              onSnoozedClick={handleSnoozedStatsClick}
              onIncassoViaDerdeClick={handleIncassoViaDerdeStatsClick}
            />
          </div>
        )}

        {/* Debt evolution chart */}
        <div className="px-6 pb-4">
          <IncassoDebtChart />
        </div>

        {/* Table — only show when a snapshot exists */}
        {stats && !stats.lastSnapshotAt ? (
          <div className="mx-6 flex flex-col items-center justify-center rounded-lg border border-neutral-200 bg-white py-16 px-6">
            <ArrowsClockwise size={32} className="text-neutral-secondary-mid mb-3" />
            <p className="text-sm font-medium text-neutral-700 mb-1">
              Nog geen snapshot data beschikbaar
            </p>
            <p className="text-xs text-neutral-secondary-mid mb-4">
              Klik op &quot;Vernieuwen&quot; om de eerste snapshot te maken. Daarna worden de dossiers hier getoond.
            </p>
            <button
              onClick={async () => {
                await handleRefresh();
                fetchTickets();
              }}
              disabled={isRefreshing}
              className={cn(
                'flex items-center gap-1.5 rounded-full border border-neutral-200 px-4 py-2 text-sm font-medium transition-colors',
                isRefreshing
                  ? 'cursor-not-allowed text-neutral-400'
                  : 'text-neutral-700 hover:bg-neutral-50',
              )}
            >
              <ArrowsClockwise
                size={14}
                className={isRefreshing ? 'animate-spin' : ''}
              />
              {isRefreshing ? 'Vernieuwen...' : 'Vernieuwen'}
            </button>
          </div>
        ) : (
          <UniversalTable
            columnsConfig={INCASSO_LIST_COLUMNS}
            sortConfig={INCASSO_SORT_CONFIG}
            rows={tickets}
            title=""
            groupBy="none"
            isLoading={isLoading}
            renderRow={renderRow}
            customLoadingState={
              <PixelLogo size={360} text="Dossiers laden..." fullPage />
            }
          />
        )}

        {/* Spacer for FilterBar */}
        <div className="h-24" />
      </div>

      {/* FilterBar */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={(value) => { if (!isRestoringFromURL) setSearchText(value); }}
        searchPlaceholder="Zoek op naam, gebouw, vennootschap..."
        filterChips={filterBarChips}
        filterSections={filterBarSections}
        syncWithURL={true}
        urlParamPrefix=""
        onFiltersFromURL={handleFiltersFromURL}
        showWhenEmpty={true}
        showSpacer={false}
        rightContent={
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={total}
            onPageChange={setPage}
            isLoading={isLoading}
          />
        }
      />

      {/* Snooze Dialog */}
      <SnoozeDialog
        isOpen={snoozeModal.open}
        onClose={() => setSnoozeModal({ open: false, ticket: null })}
        ticketLabel={snoozeModal.ticket?.odooPartnerDisplayName}
        currentSnooze={
          snoozeModal.ticket?.isSnoozed && snoozeModal.ticket?.snoozeUntil
            ? { until: snoozeModal.ticket.snoozeUntil, reason: snoozeModal.ticket.snoozeReason ?? '' }
            : null
        }
        onConfirm={(snoozeUntil, reason) => {
          if (snoozeModal.ticket) handleSnooze(snoozeModal.ticket, snoozeUntil, reason).then(() => fetchTickets());
        }}
        onUnsnooze={() => {
          if (snoozeModal.ticket) handleUnsnooze(snoozeModal.ticket).then(() => fetchTickets());
        }}
      />

      {/* Phase Transition Dialog */}
      {phaseTransitionPending && (
        <PhaseTransitionDialog
          isOpen={true}
          targetPhase={phaseTransitionPending.toPhase}
          ticketLabel={phaseTransitionPending.ticket.odooPartnerDisplayName}
          isBackward={isBackwardMove(phaseTransitionPending.fromPhase, phaseTransitionPending.toPhase)}
          onConfirm={handlePhaseTransitionConfirm}
          onCancel={() => setPhaseTransitionPending(null)}
        />
      )}

      {/* Notes Edit Modal (contact/company notes) */}
      <NotesEditModal
        isOpen={isNotesModalOpen}
        onClose={() => { setIsNotesModalOpen(false); setNotesInvoice(null); }}
        invoice={notesInvoice}
        onNotesUpdated={handleNotesUpdated}
      />

      {/* Incasso Action Modal */}
      <IncassoActionModal
        isOpen={actionModal !== null}
        onClose={() => { setActionModal(null); handleModalClose(); }}
        invoice={ticketToInvoice(actionModal)}
        onActionCreated={() => {
          if (actionModal) {
            setTickets((prev) =>
              prev.map((t) =>
                t.id === actionModal.id
                  ? { ...t, incassoActionCount: (t.incassoActionCount || 0) + 1 }
                  : t,
              ),
            );
          }
        }}
      />

      {/* Legal Procedure Modal */}
      <LegalProcedureModal
        isOpen={legalModal !== null}
        onClose={() => { setLegalModal(null); handleModalClose(); }}
        invoice={ticketToInvoice(legalModal)}
        onProceduresChanged={() => {
          const partnerId = legalModal?.odooPartnerId;
          if (partnerId) fetchLegalSummaries([partnerId]);
        }}
      />

      {/* Payment Plan Modal */}
      <PaymentPlanModal
        isOpen={paymentPlanModal !== null}
        onClose={() => { setPaymentPlanModal(null); handleModalClose(); }}
        invoice={ticketToInvoice(paymentPlanModal)}
        onPaymentPlansChanged={() => {
          const partnerId = paymentPlanModal?.odooPartnerId;
          if (partnerId) fetchPaymentPlanSummaries([partnerId]);
        }}
      />
    </>
  );
};

export const IncassoListPage = memo(IncassoListPageComponent);
